/**
 * How long Voolist waits between listings it posts from the seller's browser, so a run does not read as a
 * script to the marketplace.
 *
 * A bulk run waits a few seconds after each listing. Some marketplaces ask for more: OfferUp's rules forbid
 * posting through emulators and third-party sites and limit how many posts an account makes, and Vestiaire
 * reviews every listing by hand, so their listings are spaced like a seller listing by hand, in a bulk run
 * and in Delist & Relist alike.
 */

/** A bulk run's pause after each listing, whatever marketplaces it went to. */
const RUN_PAUSE = Object.freeze({ base: 2000, jitter: 3000 });

/** The marketplaces that ask for a pace of their own between listings. */
const MARKETPLACE_PACE = Object.freeze({
  offerup: Object.freeze({ base: 20000, jitter: 10000 }),
  // Vestiaire closes new accounts that post many items fast, so its listings go two minutes and more apart.
  vestiaire: Object.freeze({ base: 120000, jitter: 30000 }),
});

const lengthOf = (pause, random) => pause.base + Math.floor(random() * pause.jitter);

/**
 * The pause a marketplace asks for between two listings posted to it, in milliseconds; 0 when it asks for
 * none of its own.
 *
 * @param {string} platform
 * @param {function(): number} [random]
 * @returns {number}
 */
export function marketplacePause(platform, random = Math.random) {
  const pace = MARKETPLACE_PACE[platform];
  return pace ? lengthOf(pace, random) : 0;
}

/**
 * Spaces out the listings one run posts to the marketplaces that ask for a pace of their own, by the time since
 * the last listing posted to the same marketplace was done — whatever went to other marketplaces in between —
 * as a bulk run pauses after each listing. Voolist's own pace for a marketplace (Vestiaire's two minutes between
 * new listings) counts from a moment inside the posting before, so a pause counted from when that posting began
 * could fall short of it. Delist & Relist takes its items one by one, listing by listing, so its OfferUp items rarely
 * come one right after another.
 *
 * @param {Object} [clock] - for tests: `now()` in milliseconds, `wait(ms)`, `random()`
 * @returns {{ posting: (platform: string, post: () => Promise<*>) => Promise<*> }}
 */
export function createMarketplacePacer({
  now = () => Date.now(),
  wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); }),
  random = Math.random,
} = {}) {
  const lastDoneAt = new Map();
  return {
    /**
     * Post one listing to this marketplace (`post`) once its pause since the last listing posted to it, if it asks
     * for one, has passed; answers what `post` answered.
     */
    async posting(platform, post) {
      const last = lastDoneAt.get(platform);
      const pause = marketplacePause(platform, random);
      if (pause > 0 && last !== undefined) {
        const remaining = last + pause - now();
        if (remaining > 0) await wait(remaining);
      }
      try {
        return await post();
      } finally {
        lastDoneAt.set(platform, now());
      }
    },
  };
}

/**
 * The pause after a bulk run sent one listing to these marketplaces: the run's few seconds, or longer when
 * one of them asks for longer.
 *
 * @param {string[]} platforms - the marketplaces the listing was just sent to
 * @param {function(): number} [random]
 * @returns {number} milliseconds
 */
export function pauseAfterListing(platforms, random = Math.random) {
  return Math.max(lengthOf(RUN_PAUSE, random), ...platforms.map((platform) => marketplacePause(platform, random)));
}
